import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { emailPattern } from "../utils/patterns";

import {
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  Link,
} from "@material-ui/core";

import { useErrorCodeTranslation } from "../hooks/errors";
import { login, selectEmail } from "../store/authSlice";
import FormAlert from "./FormAlert";
import ButtonWithLoading from "./ButtonWithLoading";
import { useOperationState } from "../hooks/operationsState";

import {
  FIELD_REQUIRED,
  INVALID_EMAIL_ADDRESS,
  TOO_MANY_REQUESTS,
} from "../constants/errors";

function LoginForm() {
  const { t } = useTranslation("common");
  const { et } = useErrorCodeTranslation();
  const dispatch = useDispatch();
  const [counter, setCounter] = useState(0);

  const rememberedEmail = useSelector(selectEmail);
  const {
    operationLoading,
    operationError,
    clearOperation,
  } = useOperationState("auth.login");

  const { handleSubmit, register, errors, control } = useForm({
    defaultValues: {
      email: rememberedEmail || "",
      rememberEmail: !!rememberedEmail,
    },
  });

  const onSubmit = (values, e) => {
    e.preventDefault();
    dispatch(login(values));
  };

  useEffect(() => {
    if (!counter) return;
    const timer = setInterval(() => setCounter(counter - 1), 1000);
    return () => clearInterval(timer);
  }, [counter]);

  useEffect(() => {
    if (
      operationError &&
      operationError.code === TOO_MANY_REQUESTS &&
      operationError.time
    ) {
      let secondsSinceEpoch = Math.round(new Date().getTime() / 1000);
      let retryAfterInSeconds = operationError.time - secondsSinceEpoch;
      if (retryAfterInSeconds > 0) {
        setCounter(operationError.time - secondsSinceEpoch);
      }
    }
  }, [setCounter, operationError]);

  useEffect(() => {
    clearOperation();
  }, [clearOperation]);

  useEffect(() => {
    if (counter === 0) {
      clearOperation();
    }
  }, [counter, clearOperation]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Controller
        id="email"
        as={TextField}
        rules={{
          required: et(FIELD_REQUIRED),
          pattern: {
            value: emailPattern,
            message: et(INVALID_EMAIL_ADDRESS),
          },
        }}
        control={control}
        margin="normal"
        fullWidth
        name="email"
        label={t("Email address")}
        type="email"
        autoComplete="email"
        autoFocus={!Boolean(rememberedEmail)}
        error={!!errors.email}
        helperText={errors.email && errors.email.message}
        disabled={operationLoading}
        data-testid="username"
      />

      <TextField
        id="password"
        margin="normal"
        fullWidth
        name="password"
        type="password"
        label={t("Password")}
        autoComplete="current-password"
        autoFocus={Boolean(rememberedEmail)}
        error={!!errors.password}
        helperText={errors.password && errors.password.message}
        inputRef={register({
          required: et(FIELD_REQUIRED),
        })}
        disabled={operationLoading}
        data-testid="password"
      ></TextField>
      {operationError && (
        <FormAlert severity="error" fullWidth data-testid="login-error">
          {operationError.code === TOO_MANY_REQUESTS
            ? et(operationError.code, {
                $time: counter ? `${counter} ${t("seconds")}` : t("short time"),
              })
            : et(operationError.code)}
        </FormAlert>
      )}

      <FormControlLabel
        control={
          <Controller
            control={control}
            as={Checkbox}
            name="rememberEmail"
            color="primary"
            disabled={operationLoading}
          />
        }
        label={t("Remember my username")}
      />

      <ButtonWithLoading
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        mb={4}
        loading={operationLoading}
        disabled={counter > 0}
        data-testid="submit-button"
      >
        {t("Login")}
      </ButtonWithLoading>
      <Typography component="p" variant="body1" align="center">
        {t("Don't have an account yet?")}{" "}
        <Link component="a" href="https://volt.io/contact" target="_blank">
          {t("Sign Up")}
        </Link>
      </Typography>
    </form>
  );
}

export default LoginForm;
